import { IconLoader2 } from '@tabler/icons-react';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  getInternalFeatureFlags,
  type InternalFeatureFlag,
  setFeatureFlag,
} from '@/api/feature-flags';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

export const FeatureFlags: React.FC = () => {
  const [flags, setFlags] = useState<InternalFeatureFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [togglingCode, setTogglingCode] = useState<string | null>(null);

  const loadFlags = useCallback(async () => {
    try {
      const data = await getInternalFeatureFlags();
      setFlags(data);
    } catch {
      toast.error('Failed to load feature flags');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const handleSetFlag = async (code: string, enabled: boolean) => {
    setTogglingCode(code);
    try {
      const result = await setFeatureFlag(code, enabled);
      setFlags((prev) =>
        prev.map((f) => (f.code === code ? { ...f, enabled: result.enabled } : f))
      );
      toast.success(
        `${flags.find((f) => f.code === code)?.name ?? code} has been ${result.enabled ? 'enabled' : 'disabled'}`
      );
    } catch {
      toast.error(`Failed to update ${code}`);
    } finally {
      setTogglingCode(null);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Feature Flags</h1>
          <p className="text-muted-foreground mt-1">
            Enable or disable features for this environment.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <IconLoader2 className="animate-spin size-4" />
            <span>Loading flags…</span>
          </div>
        ) : flags.length === 0 ? (
          <p className="text-muted-foreground">No feature flags configured.</p>
        ) : (
          <Card>
            <CardContent className="p-0">
              {flags.map((flag) => (
                <div
                  key={flag.code}
                  className="flex items-center justify-between px-6 py-4 border-b border-border last:border-b-0"
                >
                  <div className="space-y-0.5 flex-1">
                    <Label className="text-sm font-medium">{flag.name}</Label>
                    {flag.description && (
                      <p className="text-sm text-muted-foreground">{flag.description}</p>
                    )}
                  </div>
                  <div className="flex items-center ml-4">
                    {togglingCode === flag.code ? (
                      <IconLoader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    ) : (
                      <Switch
                        checked={flag.enabled}
                        onCheckedChange={(checked) => handleSetFlag(flag.code, checked)}
                        aria-label={`Toggle ${flag.name}`}
                      />
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};
